import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const Th = ({ children, align = 'left' }) => (
    <th style={{
        padding: '10px 14px', fontSize: 11, fontWeight: 700,
        color: '#9ca3af', textTransform: 'uppercase',
        letterSpacing: '0.4px', textAlign: align,
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </th>
);

const Cell = ({ children, align = 'left', muted }) => (
    <td style={{
        padding: '10px 14px', fontSize: 13,
        color: muted ? '#9ca3af' : '#374151',
        fontWeight: muted ? 400 : 500,
        textAlign: align,
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    }}>
        {children ?? '—'}
    </td>
);

const fmtWt = (n) => (n != null ? `${parseFloat(n).toFixed(2)} T` : '—');

const formatDate = (d) => {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return d;
    }
};

const ChallanDailyTable = ({ items = [], loading }) => {
    const { t } = useTranslation();
    const maxCount = useMemo(() =>
        Math.max(...items.map((r) => r.total_challans ?? 0), 1),
        [items]
    );

    return (
        <div style={cardStyle}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('challanAnalytics.tables.dailyTrend')}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{t('challanAnalytics.subtitles.dailyActivity')}</div>
                </div>
                {items.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', backgroundColor: '#f5f3ff', borderRadius: 20, padding: '3px 10px' }}>
                        {items.length} {t('challanAnalytics.labels.daysCountSuffix')}
                    </span>
                )}
            </div>

            {loading && <EmptyState message={t('challanAnalytics.emptyStates.loadingDaily')} />}
            {!loading && !items.length && <EmptyState message={t('challanAnalytics.emptyStates.noDailyData')} />}

            {!loading && items.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 650 }}>
                        <thead>
                            <tr>
                                <Th>#</Th>
                                <Th>{t('common.analyticsColumns.date')}</Th>
                                <Th>{t('common.analyticsColumns.challans')}</Th>
                                <Th align="right">{t('common.analyticsColumns.weight')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((row, i) => {
                                const count = row.total_challans ?? 0;
                                const countPct = (count / maxCount) * 100;

                                return (
                                    <tr
                                        key={row.challan_date ?? i}
                                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#faf5ff')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#ffffff' : '#fafafa')}
                                    >
                                        <Cell muted>{i + 1}</Cell>
                                        <Cell>
                                            <span style={{ fontWeight: 600, color: '#374151' }}>
                                                {formatDate(row.challan_date)}
                                            </span>
                                        </Cell>
                                        <Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 14, fontWeight: 700, color: '#0891b2', minWidth: 28 }}>
                                                    {count}
                                                </span>
                                                <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e5e7eb', minWidth: 60 }}>
                                                    <div style={{
                                                        height: '100%', borderRadius: 3,
                                                        backgroundColor: '#0891b2',
                                                        width: `${countPct}%`,
                                                        transition: 'width 0.4s',
                                                    }} />
                                                </div>
                                            </div>
                                        </Cell>
                                        <Cell align="right">
                                            {fmtWt(row.total_weight_tons)}
                                        </Cell>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const EmptyState = ({ message }) => (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>{message}</div>
);

const cardStyle = {
    backgroundColor: '#fff', borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};

export default ChallanDailyTable;
